// Redux/Slices/Pages/Payment/EnquirySlice.js
// Enquiry-First Booking Flow — No payment required at request time.
// Lifecycle: Customer Requests → Provider Accepts → Customer Pays

import { createSlice } from '@reduxjs/toolkit';
import { axiosInstance } from '../../../../Common/Interceptors/AxiosConfig';
import { showNotification } from '../../../../Common/Toast/Toast';

const initialState = {
  isLoading: false,
  errors: null,
  enquiry: null,        // The created booking record
  isSuccess: false,
};

const EnquirySlice = createSlice({
  name: 'enquiry',
  initialState,
  reducers: {
    setLoading: (state, action) => { state.isLoading = action.payload; },
    setErrors: (state, action) => { state.errors = action.payload; },
    setEnquiry: (state, action) => { state.enquiry = action.payload; },
    setSuccess: (state, action) => { state.isSuccess = action.payload; },
    resetEnquiry: () => initialState,
  },
});

/**
 * submitEnquiry — Manual Thunk
 * Calls POST /api/v2/bookings with bookingModel: 'ENQUIRY'
 * No payment is triggered. Provider is notified separately.
 *
 * @param {object} payload - { serviceId, workspaceId, notes, contactPhone, preferredDate }
 */
export const submitEnquiry = (payload) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setErrors(null));
  dispatch(setSuccess(false));

  try {
    const response = await axiosInstance.post('/bookings/enquiry', {
      serviceId: payload.serviceId,
      workspaceId: payload.workspaceId,
      notes: payload.notes || '',
      contactPhone: payload.contactPhone || '',
      preferredDate: payload.preferredDate || '',
      preferredTime: payload.preferredTime || '', // Move to top level for backend fallback
      bookingSlotTime: payload.bookingSlotTime, // include the new timestamp
      metadata: {
        source: 'enquiry-form',
      },
    });

    const { success, data, message } = response?.data || {};

    if (success) {
      dispatch(setEnquiry(data));
      dispatch(setSuccess(true));

      showNotification({
        message: '🎉 Request sent! The provider will respond shortly.',
        type: 'success',
        theme: 'light',
      });

      return { success: true, data };
    }

    throw new Error(message || 'Request failed');
  } catch (error) {
    const errorMsg = error?.response?.data?.message || error.message || 'Something went wrong';
    dispatch(setErrors(errorMsg));

    showNotification({
      message: errorMsg,
      type: 'error',
      theme: 'light',
    });

    return { success: false, error: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

export const { setLoading, setErrors, setEnquiry, setSuccess, resetEnquiry } = EnquirySlice.actions;

export default EnquirySlice.reducer;
